import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/upr-api";
import { toast } from "sonner";
import { Upload, Link2 } from "lucide-react";

/**
 * Reusable upload widget for the admin panel.
 * Lets the admin either paste a URL or upload a file (image / audio).
 *
 * Props:
 *  - value: current URL string
 *  - onChange: (newUrl) => void
 *  - kind: "image" | "audio"
 *  - label: string
 *  - testid: prefix for data-testid
 */
export default function FileUpload({ value, onChange, kind = "image", label = "File", testid = "upload" }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/upload", fd);
      onChange(data.url);
      toast.success(`${kind === "audio" ? "Audio" : "Image"} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const accept = kind === "audio"
    ? "audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/flac,audio/aiff,audio/x-aiff,audio/aif,audio/mp4,audio/m4a,.mp3,.wav,.ogg,.flac,.aif,.aiff,.m4a"
    : "image/jpeg,image/png,image/webp,image/gif,image/svg+xml,.jpg,.jpeg,.png,.webp,.gif,.svg";

  return (
    <div>
      <span className="upr-eyebrow block mb-2">{label}</span>
      <div className="flex items-stretch gap-2">
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={kind === "audio" ? "Paste audio URL or upload" : "Paste image URL or upload"}
          data-testid={`${testid}-url`}
          className="flex-1 p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
          style={{ borderColor: "var(--upr-border)" }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          data-testid={`${testid}-btn`}
          className="upr-btn upr-btn-ghost shrink-0"
          title="Upload file"
        >
          {busy ? "Uploading…" : (<><Upload size={14} /> Upload</>)}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFile}
          className="hidden"
          data-testid={`${testid}-file`}
        />
      </div>
      {value && kind === "image" && (
        <div className="mt-2 inline-block" style={{ border: "1px solid var(--upr-border)" }}>
          <img src={value} alt="preview" className="block w-20 h-20 object-cover" />
        </div>
      )}
      {value && kind === "audio" && (
        <audio src={value} controls className="mt-2 w-full max-w-md" />
      )}
    </div>
  );
}
